import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { SQRT3, sizeFromCellCount } from '../constants';
import { applyModifier, badRestrictionCells, checkSolved, computeConstraintStatuses } from '../rules';
import { gameReducer, type GameState } from '../state';
import { BagTile, HexBoard, type DragState } from './HexBoard';
import { Bag } from './Bag';
import { HintArrow } from './HintArrow';
import { TUTORIAL_STAGES, TUTORIAL_TOTAL_STEPS, type TutorialStage, type TutorialStep } from '../tutorial/tutorialData';

const DRAG_THRESHOLD = 5;

interface TutorialProps {
  onClose: () => void;
}

// Build a fresh GameState for the given stage. Independent from the main
// app state — uses its own reducer so the real game's progress isn't
// touched.
function makeStateFor(stage: TutorialStage): GameState {
  return {
    difficulty: stage.puzzle.difficulty,
    puzzleDate: 'tutorial',
    name: stage.puzzle.name,
    cells: stage.puzzle.cells.map(c => ({ ...c })),
    bag: stage.puzzle.bag.map(t => ({ ...t, placed: false })),
    constraints: stage.puzzle.constraints,
    selectedTileId: null,
    history: [],
    startedAt: Date.now(),
    solved: false,
    elapsedAtSolve: 0,
    recentlyPlaced: null,
  };
}

// Sum of step counts for stages [0..stageIdx-1] — used for the
// "Step N of M" indicator across stages.
function globalStepNumber(stageIdx: number, stepIdx: number): number {
  let n = 0;
  for (let i = 0; i < stageIdx; i++) n += TUTORIAL_STAGES[i].steps.length;
  return n + stepIdx + 1;
}

export function Tutorial({ onClose }: TutorialProps) {
  const [stageIdx, setStageIdx] = useState(0);
  // initialStepIdx lets `Back` from the start of stage N land on the
  // last step of stage N-1.
  const [initialStepIdx, setInitialStepIdx] = useState(0);

  const goToNextStage = useCallback(() => {
    setStageIdx(i => {
      const next = i + 1;
      if (next >= TUTORIAL_STAGES.length) {
        onClose();
        return i;
      }
      setInitialStepIdx(0);
      return next;
    });
  }, [onClose]);

  const goToPrevStage = useCallback(() => {
    setStageIdx(i => {
      if (i === 0) return i;
      const prev = i - 1;
      setInitialStepIdx(TUTORIAL_STAGES[prev].steps.length - 1);
      return prev;
    });
  }, []);

  const stage = TUTORIAL_STAGES[stageIdx];

  return (
    <div className="hm-tutorial-overlay" role="dialog" aria-modal="true" aria-label="Hexponents tutorial">
      <StagePlayer
        key={stage.id}
        stage={stage}
        stageIdx={stageIdx}
        startStepIdx={initialStepIdx}
        onSkip={onClose}
        onAdvanceStage={goToNextStage}
        onBackStage={goToPrevStage}
      />
    </div>
  );
}

// Inner player: owns the reducer + step index for ONE stage. Remounts
// (via React `key`) on stage change, which gives us a clean reducer
// reset for free.
function StagePlayer({
  stage, stageIdx, startStepIdx,
  onSkip, onAdvanceStage, onBackStage,
}: {
  stage: TutorialStage;
  stageIdx: number;
  startStepIdx: number;
  onSkip: () => void;
  onAdvanceStage: () => void;
  onBackStage: () => void;
}) {
  const [state, dispatch] = useReducer(gameReducer, stage, makeStateFor);
  const [stepIdx, setStepIdx] = useState(startStepIdx);
  const step: TutorialStep | undefined = stage.steps[stepIdx];
  const isLastStage = stageIdx === TUTORIAL_STAGES.length - 1;
  const isLastStepOfStage = stepIdx === stage.steps.length - 1;
  const isFinalStep = !!(step?.isFinal && isLastStage);

  const stepStartRef = useRef<number>(Date.now());

  // Snapshot the game state at the moment we LAND on each step (before
  // any user action in that step). Lets Back rewind to a state where the
  // current step's predicate is still false — otherwise a predicate
  // satisfied by the prior step's action would fire immediately and
  // bounce the user back forward.
  const snapshotsRef = useRef<Record<number, GameState>>({});
  // Seed snapshot[startStepIdx] = initial state on mount.
  if (snapshotsRef.current[startStepIdx] === undefined) {
    snapshotsRef.current[startStepIdx] = state;
  }

  const evaluatedConstraints = useMemo(
    () => computeConstraintStatuses(state.cells, state.constraints),
    [state.cells, state.constraints]
  );
  const badRestr = useMemo(() => badRestrictionCells(state.cells), [state.cells]);

  // Mark solved (mirroring App.tsx) so stage-1's solved predicate can fire.
  useEffect(() => {
    if (state.solved) return;
    if (state.bag.length === 0) return; // passive stages have no bag
    if (checkSolved(state.cells, evaluatedConstraints, state.bag)) {
      dispatch({ type: 'MARK_SOLVED', elapsed: Date.now() - state.startedAt });
    }
  }, [state.cells, state.bag, state.solved, state.startedAt, evaluatedConstraints]);

  // When advanceStep fires, the next step's predicate effect runs once
  // on the new step's first render — with the SAME state that just
  // satisfied the previous step. If that state happens to also satisfy
  // the next step's predicate, the user sees a multi-step skip. Block
  // the next predicate-check tick so each user action advances at most
  // one step.
  const skipNextPredicateRef = useRef(false);

  const advanceStep = useCallback(() => {
    // Record the state ENTERING the next step (i.e. the state right after
    // the action that just satisfied the current step). Back will read
    // this to rewind.
    const nextIdx = stepIdx + 1;
    if (snapshotsRef.current[nextIdx] === undefined) {
      snapshotsRef.current[nextIdx] = state;
    }
    skipNextPredicateRef.current = true;
    if (nextIdx < stage.steps.length) {
      setStepIdx(nextIdx);
    } else {
      onAdvanceStage();
    }
  }, [stage, stepIdx, state, onAdvanceStage]);

  // Predicate-driven step advancement.
  useEffect(() => {
    if (!step) return;
    if (step.advance.kind !== 'predicate') return;
    if (skipNextPredicateRef.current) {
      skipNextPredicateRef.current = false;
      return;
    }
    if (step.advance.check(state, evaluatedConstraints)) {
      advanceStep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, evaluatedConstraints, step]);

  // Per-step timeout fallback (predicate steps only).
  useEffect(() => {
    stepStartRef.current = Date.now();
    if (!step || step.advance.kind !== 'predicate' || !step.advance.timeoutMs) return;
    const t = setTimeout(() => advanceStep(), step.advance.timeoutMs);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIdx]);

  // Clear placement-flash highlight (matches App.tsx).
  useEffect(() => {
    if (state.recentlyPlaced == null) return;
    const t = setTimeout(() => dispatch({ type: 'CLEAR_PLACE_HIGHLIGHT' }), 320);
    return () => clearTimeout(t);
  }, [state.recentlyPlaced]);

  const goBack = useCallback(() => {
    if (stepIdx > 0) {
      const prevIdx = stepIdx - 1;
      const snap = snapshotsRef.current[prevIdx];
      // Forward snapshots are stale once we rewind — drop them so the
      // next forward traversal recaptures fresh state.
      for (const k of Object.keys(snapshotsRef.current)) {
        if (Number(k) > prevIdx) delete snapshotsRef.current[Number(k)];
      }
      if (snap) dispatch({ type: 'RESTORE', snapshot: snap });
      setStepIdx(prevIdx);
    } else {
      onBackStage();
    }
  }, [stepIdx, onBackStage]);

  // ─────────────── Drag state (mirrors App.tsx) ───────────────
  const [drag, setDrag] = useState<DragState | null>(null);

  const handleTilePointerDown = useCallback((tile: { id: string; value: number; placed?: boolean }, e: React.PointerEvent) => {
    if (state.solved) return;
    if (tile.placed) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    setDrag({
      type: 'bag',
      tileId: tile.id,
      value: tile.value,
      startX: e.clientX, startY: e.clientY,
      x: e.clientX, y: e.clientY,
      active: false,
      overIdx: null,
      overBag: false,
    });
  }, [state.solved]);

  const handleCellPointerDown = useCallback((cellIdx: number, e: React.PointerEvent) => {
    if (state.solved) return;
    const cell = state.cells[cellIdx];
    if (!cell || cell.kind !== 'placed') return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    setDrag({
      type: 'cell',
      cellIdx,
      value: cell.modifier != null && cell.value != null
        ? (applyModifier(cell.value, cell.modifier) ?? undefined)
        : cell.value,
      rawValue: cell.value,
      startX: e.clientX, startY: e.clientY,
      x: e.clientX, y: e.clientY,
      active: false,
      overIdx: null,
      overBag: false,
    });
  }, [state.solved, state.cells]);

  useEffect(() => {
    if (!drag) return;

    const onMove = (e: PointerEvent) => {
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const active = drag.active || dist > DRAG_THRESHOLD;
      let overIdx: number | null = null;
      let overBag = false;
      if (active) {
        const target = document.elementFromPoint(e.clientX, e.clientY);
        const cellEl = target && (target as Element).closest && (target as Element).closest('[data-cell-idx]');
        if (cellEl) {
          const idx = parseInt(cellEl.getAttribute('data-cell-idx')!, 10);
          if (!Number.isNaN(idx)) overIdx = idx;
        } else if (target && (target as Element).closest && (target as Element).closest('[data-bag-drop]')) {
          overBag = true;
        }
      }
      setDrag(d => d ? { ...d, x: e.clientX, y: e.clientY, active, overIdx, overBag } : null);
    };

    const onUp = (e: PointerEvent) => {
      const wasActive = drag.active ||
        Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY) > DRAG_THRESHOLD;

      if (wasActive) {
        const target = document.elementFromPoint(e.clientX, e.clientY);
        const cellEl = target && (target as Element).closest && (target as Element).closest('[data-cell-idx]');
        const bagEl  = target && (target as Element).closest && (target as Element).closest('[data-bag-drop]');
        if (cellEl) {
          const idx = parseInt(cellEl.getAttribute('data-cell-idx')!, 10);
          if (!Number.isNaN(idx)) {
            if (drag.type === 'bag' && drag.tileId) {
              dispatch({ type: 'DRAG_FROM_BAG', tileId: drag.tileId, cellIdx: idx });
            } else if (drag.type === 'cell' && drag.cellIdx != null && idx !== drag.cellIdx) {
              dispatch({ type: 'MOVE_CELL', fromIdx: drag.cellIdx, toIdx: idx });
            }
          }
        } else if (bagEl && drag.type === 'cell' && drag.cellIdx != null) {
          dispatch({ type: 'PLACE_OR_PICKUP', cellIdx: drag.cellIdx });
        }
        const suppress = (ev: Event) => { ev.stopPropagation(); ev.preventDefault(); };
        document.addEventListener('click', suppress, { capture: true, once: true });
        setTimeout(() => {
          document.removeEventListener('click', suppress, { capture: true });
        }, 50);
      }
      setDrag(null);
    };

    const onCancel = () => setDrag(null);

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onCancel);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onCancel);
    };
  }, [drag]);

  const handleSelectTile = (id: string) => dispatch({ type: 'SELECT_TILE', tileId: id });
  const handleCellClick  = (i: number)  => dispatch({ type: 'PLACE_OR_PICKUP', cellIdx: i });

  const clickableCells = useMemo(() => {
    const set = new Set<number>();
    state.cells.forEach((c, i) => {
      if (c.kind === 'placed') set.add(i);
      else if (c.kind === 'empty' && state.selectedTileId) set.add(i);
    });
    return set;
  }, [state.cells, state.selectedTileId]);

  const draggableCells = useMemo(() => {
    const set = new Set<number>();
    state.cells.forEach((c, i) => {
      if (c.kind === 'placed') set.add(i);
    });
    return set;
  }, [state.cells]);

  // ─────────────── Board sizing ───────────────
  const boardWrapRef = useRef<HTMLDivElement | null>(null);
  const [boardBox, setBoardBox] = useState({ w: 480, h: 360 });
  const [vw, setVw] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const onR = () => setVw(window.innerWidth);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);
  useEffect(() => {
    if (!boardWrapRef.current || typeof ResizeObserver === 'undefined') return;
    const el = boardWrapRef.current;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) setBoardBox({ w: width, h: height });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { boardR, boardPad } = useMemo(() => {
    const size = sizeFromCellCount(state.cells.length);
    const numColsM1 = 2 * size - 2;
    const widthCoef = 3 * size - 1;
    const heightCoef = (2 * size - 1) * SQRT3;
    const minR = vw < 480 ? 18 : 24;
    const maxR = vw < 480 ? 32 : vw < 720 ? 40 : 48;
    const padW = vw < 480 ? 64 : 72;
    const padH = vw < 480 ? 32 : 48;
    const gap = 5;
    const Rw = (boardBox.w - 2 * padW - numColsM1 * gap) / widthCoef;
    const Rh = (boardBox.h - 2 * padH - numColsM1 * gap) / heightCoef;
    let R = Math.min(Rw, Rh, maxR);
    if (!Number.isFinite(R) || R < minR) R = minR;
    return { boardR: R, boardPad: Math.max(padH, padW) };
  }, [boardBox, vw, state.cells.length]);

  // ─────────────── Step-driven highlights ───────────────
  // Build the HintMove shape that HintArrow expects when an arrow is
  // requested. We only ever surface 'place' arrows in the tutorial.
  const arrowHint = useMemo(() => {
    if (!step?.arrow) return null;
    return { kind: 'place' as const, tileId: step.arrow.tileId, cellIdx: step.arrow.toCell };
  }, [step]);

  const stepNumber = globalStepNumber(stageIdx, stepIdx);
  const showNextButton = step?.advance.kind === 'next';
  const canGoBack = !(stageIdx === 0 && stepIdx === 0);
  const nextLabel = isFinalStep ? 'Done' : (isLastStepOfStage ? 'Continue' : 'Next');

  return (
    <div className="hm-tutorial-card" onClick={e => e.stopPropagation()}>
      <header className="hm-tutorial-header">
        <span className="hm-tutorial-step-indicator">
          {stage.title} · {stepNumber}/{TUTORIAL_TOTAL_STEPS}
        </span>
        <button
          type="button"
          className="hm-tutorial-skip"
          onClick={onSkip}
        >
          Skip tutorial
        </button>
      </header>

      <div className="hm-tutorial-message">
        <p>{step?.message}</p>
      </div>

      <div className="hm-tutorial-board-wrap" ref={boardWrapRef}>
        <HexBoard
          cells={state.cells}
          evaluatedConstraints={evaluatedConstraints}
          badRestr={badRestr}
          R={boardR}
          pad={boardPad}
          gap={5}
          onCellClick={handleCellClick}
          onCellPointerDown={handleCellPointerDown}
          clickableCells={clickableCells}
          draggableCells={draggableCells}
          recentlyPlaced={state.recentlyPlaced}
          drag={drag}
          dragOverIdx={drag && drag.active ? drag.overIdx : null}
          forcedPinnedLine={step?.chipHighlight ?? null}
          hintCellIdx={step?.cellHighlight ?? null}
          hintKind={step?.cellHighlight != null ? 'place' : null}
        />
      </div>

      {state.bag.length > 0 && (
        <div className="hm-tutorial-bag-wrap">
          <Bag
            tiles={state.bag}
            selectedId={state.selectedTileId}
            onSelectTile={handleSelectTile}
            onTilePointerDown={handleTilePointerDown}
            draggingTileId={drag && drag.active && drag.type === 'bag' ? (drag.tileId ?? null) : null}
            isReturnTarget={!!drag && drag.active && drag.type === 'cell' && !!drag.overBag}
            hintTileId={step?.arrow?.tileId ?? null}
            tileSize={vw < 480 ? 32 : 38}
          />
        </div>
      )}

      <footer className="hm-tutorial-footer">
        <button
          type="button"
          className="hm-tutorial-back"
          onClick={goBack}
          disabled={!canGoBack}
          aria-label="Previous step"
        >
          ‹ Back
        </button>
        <span className="hm-tutorial-stage-count">
          {stageIdx + 1}/{TUTORIAL_STAGES.length}
        </span>
        {showNextButton ? (
          <button
            type="button"
            className="hm-primary hm-tutorial-next"
            onClick={advanceStep}
          >
            {nextLabel} ›
          </button>
        ) : (
          <span className="hm-tutorial-hint" aria-live="polite">
            try the move →
          </span>
        )}
      </footer>

      {drag && drag.active && drag.value != null && (
        <div
          className="hm-drag-ghost"
          style={{ left: drag.x, top: drag.y }}
        >
          <BagTile value={drag.value} size={Math.max(36, boardR * 1.4)} />
        </div>
      )}

      {/* Arrow draws OUTSIDE the card (position: fixed) — it spans
          from the bag tile (rendered in this overlay) to the target
          board cell (also rendered in this overlay). The main app's
          bag/board are still mounted behind us; scope the query so we
          only see the tutorial's elements. */}
      <HintArrow hint={arrowHint} scopeSelector=".hm-tutorial-card" />
    </div>
  );
}
